import { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { renderHtmlsToPdfBlob } from '../utils/pdfExport';

const inputCls = 'w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600';

/**
 * Envoi d'un document par e-mail : destinataire / objet / message modifiables,
 * aperçu du document avant envoi, PDF généré et mis à disposition via un lien sécurisé.
 */
export function EnvoiDocumentModal({ html, fileName, libelle, defaultTo, defaultSujet, defaultMessage, onClose, onSent }: {
  html: string; fileName: string; libelle: string;
  defaultTo?: string; defaultSujet: string; defaultMessage: string;
  onClose: () => void; onSent?: () => void;
}) {
  const [to, setTo] = useState(defaultTo || '');
  const [sujet, setSujet] = useState(defaultSujet);
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  const envoyer = async () => {
    if (!/^\S+@\S+\.\S+$/.test(to.trim())) { toast.error('Adresse e-mail du destinataire invalide'); return; }
    setSending(true);
    try {
      const blob = await renderHtmlsToPdfBlob([html], { fileName, rootId: `envoi-${Date.now()}` });
      const path = `envois/${crypto.randomUUID()}/${fileName}`;
      const { error: upErr } = await supabase.storage.from('ged').upload(path, blob, { contentType: 'application/pdf' });
      if (upErr) throw upErr;
      const { data: signed, error: sErr } = await supabase.storage.from('ged').createSignedUrl(path, 60 * 60 * 24 * 60, { download: fileName });
      if (sErr || !signed) throw sErr || new Error('Lien indisponible');
      const { error } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'document-client',
          recipientEmail: to.trim(),
          idempotencyKey: `doc-${path}`,
          templateData: { sujet, message, lienDocument: signed.signedUrl, libelleDocument: libelle },
        },
      });
      if (error) throw error;
      toast.success(`E-mail envoyé à ${to.trim()}`);
      onSent?.();
      onClose();
    } catch (e: any) {
      toast.error(`Envoi impossible : ${e?.message || 'erreur inconnue'}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-6xl my-8 grid lg:grid-cols-2" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg text-slate-100 flex items-center gap-2"><Send size={18} strokeWidth={1.5} className="text-amber-400" /> Envoyer {libelle}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200 lg:hidden"><X size={18} strokeWidth={1.5} /></button>
          </div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-400">Destinataire</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@exemple.fr" className={inputCls} />
          <label className="block text-[10px] uppercase tracking-widest text-slate-400">Objet</label>
          <input value={sujet} onChange={(e) => setSujet(e.target.value)} className={inputCls} />
          <label className="block text-[10px] uppercase tracking-widest text-slate-400">Message</label>
          <textarea rows={12} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} leading-relaxed`} />
          <p className="text-[11px] text-slate-500">Le PDF est joint sous forme de bouton « Télécharger » dans l’e-mail (lien sécurisé valable 60 jours).</p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
            <button onClick={envoyer} disabled={sending} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 rounded-md text-sm font-medium flex items-center gap-2">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.5} />} Envoyer
            </button>
          </div>
        </div>
        <div className="hidden lg:block bg-white rounded-r-xl overflow-hidden min-h-[640px]">
          <iframe title="Aperçu avant envoi" srcDoc={html} className="w-full h-full min-h-[640px] border-0" sandbox="" />
        </div>
      </div>
    </div>
  );
}
